/**
 * "Recent creations" — the right-hand column of the Studio tab (stacked under the trends below 1024px). Only what was
 * just made: generations in progress / failed, then the latest RECENT_LIMIT sounds — no search or filters; "See all"
 * opens Your Collections › All sounds. From 1024px it sits in the page grid as a subgrid of three rows shared with
 * "Trending on TikTok", so both columns line up:
 *   A. the title, what the panel holds and the originals note;  B. the toolbar — Team / Mine and "See all";
 *   C. the panel — sticky, scrolling on its own — holding ONE list, or the trend `stage` when the page puts one there:
 *   - generations in progress at the top (skeleton cover, name, "Like “<trend>” · 30s", thin progress bar, %);
 *   - when one finishes its sound(s) take that row's place — highlighted, with a "New" pill, scrolled into view;
 *   - failed generations as a compact row (error + Retry / Dismiss);
 *   - then the team's most recent sounds (Team / Mine), each a 56px row with play on the cover and one ⋯ menu.
 * A sound is never listed twice: a finished job's tracks and the recent list are merged by track id, and the job is
 * dropped from the session once the recent list has caught up. "See all" opens the full list.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import type { StudioJob, StudioTrack } from '@/api/audioStudioApi';
import { usePlayerInset } from './AudioPlayer';
import { FailedJobRow, JobRow, ScopeToggle, SoundRow, type SoundsScope } from './SoundRows';
import { useNow } from './format';
import { audioKeys, isJobActive, jobQueryOptions, useStudioTracks } from './queries';
import { startJobRequest, type StudioJobRef } from './studioJobs';
import { OriginalsNote } from './TrendBits';
import { useTrackActions } from './useTrackActions';

const RECENT_LIMIT = 8;
/** The purple background is on for a moment, then fades out over 2 s (the row's transition). */
const FLASH_MS = 300;
const NEW_PILL_MS = 10_000;

export const EMPTY_SOUNDS_TEXT = 'Your recent creations will appear here — describe one above or pick a trend.';
export const RECENT_NOTE = `Generations in progress and the ${RECENT_LIMIT} latest sounds.`;

/** What the job is making: "Like “Song”" for lookalikes, else the prompt snippet. */
function jobSubject(ref: StudioJobRef): string {
  if (ref.trendTitle) return `Like “${ref.trendTitle}”`;
  // Refs stored before `trendTitle` existed carry "Inspired by “Song”".
  const m = ref.label?.match(/^Inspired by (“.*”)$/);
  if (m) return `Like ${m[1]}`;
  return ref.label || 'Your prompt';
}

function jobSubtitle(ref: StudioJobRef, job: StudioJob | undefined): string {
  return [
    jobSubject(ref),
    ref.durationS ? `${ref.durationS}s` : null,
    job && job.requested > 1 ? `${job.requested} variations` : null,
  ].filter(Boolean).join(' · ');
}

function dedupe(tracks: StudioTrack[]): StudioTrack[] {
  const seen = new Set<string>();
  return tracks.filter((t) => (seen.has(t.track_id) ? false : (seen.add(t.track_id), true)));
}

export function YourSoundsRail({ jobs, onDismissJob, onJobStarted, onSeeAll, onScopeChange, stage = null }: {
  jobs: StudioJobRef[];
  onDismissJob: (jobId: string) => void;
  /** A retried generation (the failed row is dismissed and the new job takes its place). */
  onJobStarted: (job: StudioJobRef) => void;
  onSeeAll: (scope: SoundsScope) => void;
  /** Team / Mine was switched (the page takes a staged trend off the panel so the list shows). */
  onScopeChange?: (scope: SoundsScope) => void;
  /** A trend on the panel in place of the list (desktop). The list stays mounted underneath, hidden. */
  stage?: ReactNode;
}) {
  const qc = useQueryClient();
  const [scope, setScope] = useState<SoundsScope>('team');
  const tracks = useStudioTracks({ scope, limit: RECENT_LIMIT, offset: 0 });
  const jobQs = useQueries({ queries: jobs.map((j) => jobQueryOptions(j.jobId)) });
  const now = useNow();
  const inset = usePlayerInset();
  const listRef = useRef<HTMLUListElement | null>(null);

  const entries = jobs.map((ref, i) => ({ ref, job: jobQs[i]?.data, error: (jobQs[i]?.error as Error | null) ?? null }));
  const signature = entries.map((e) => `${e.ref.jobId}:${e.job?.status ?? '-'}:${e.job?.tracks.length ?? 0}`).join('|');

  // ── Job → track hand-over ─────────────────────────────────────────────────
  const seenActive = useRef(new Set<string>());
  const handled = useRef(new Set<string>());
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());
  const [flash, setFlash] = useState<ReadonlySet<string>>(new Set());
  const [scrollTo, setScrollTo] = useState<string | null>(null);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    const done: string[] = [];
    for (const { ref, job } of entries) {
      if (!job) continue;
      if (isJobActive(job)) { seenActive.current.add(ref.jobId); continue; }
      if (job.status !== 'succeeded' || handled.current.has(ref.jobId)) continue;
      handled.current.add(ref.jobId);
      void qc.invalidateQueries({ queryKey: audioKeys.tracksAll });
      // Only celebrate what finished while we watched (not a job restored already done after a reload).
      if (seenActive.current.has(ref.jobId)) done.push(...job.tracks.map((t) => t.track_id));
    }
    if (!done.length) return;
    const add = (s: ReadonlySet<string>) => new Set([...s, ...done]);
    const remove = (s: ReadonlySet<string>) => new Set([...s].filter((id) => !done.includes(id)));
    setFresh(add);
    setFlash(add);
    setScrollTo(done[0]);
    timers.current.push(setTimeout(() => setFlash(remove), FLASH_MS), setTimeout(() => setFresh(remove), NEW_PILL_MS));
  }, [signature, qc]);

  useEffect(() => {
    if (!scrollTo) return;
    const el = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[data-track-id]') ?? []).find((n) => n.dataset.trackId === scrollTo);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    setScrollTo(null);
  }, [scrollTo]);

  // Once the recent list holds a finished job's sounds, the job has nothing left to add — drop it.
  const listTracks = useMemo(() => tracks.data?.tracks ?? [], [tracks.data]);
  useEffect(() => {
    if (tracks.isFetching) return;
    const ids = new Set(listTracks.map((t) => t.track_id));
    for (const { ref, job } of entries) {
      if (job?.status === 'succeeded' && job.tracks.length > 0 && job.tracks.every((t) => ids.has(t.track_id))) onDismissJob(ref.jobId);
    }
  }, [signature, listTracks, tracks.isFetching, onDismissJob]);

  // A deleted sound must not come back from a finished job still in the session.
  const actions = useTrackActions({
    onDeleted: (t) => entries.forEach(({ ref, job }) => { if (job?.tracks.some((x) => x.track_id === t.track_id)) onDismissJob(ref.jobId); }),
  });

  // ── Retry ─────────────────────────────────────────────────────────────────
  const [retrying, setRetrying] = useState<string | null>(null);
  const retry = useCallback(async (ref: StudioJobRef) => {
    if (!ref.request) return;
    setRetrying(ref.jobId);
    try {
      const job = await startJobRequest(ref.request);
      onJobStarted({ ...ref, jobId: job.job_id });
      onDismissJob(ref.jobId);
    } catch (e) {
      toast.error((e as Error).message || 'Could not start the generation');
    } finally {
      setRetrying(null);
    }
  }, [onDismissJob, onJobStarted]);

  // ── One list ──────────────────────────────────────────────────────────────
  const pending = entries.filter((e) => (e.job ? isJobActive(e.job) : !e.error));
  const failed = entries.filter((e) => (e.job ? e.job.status === 'failed' || (e.job.status === 'succeeded' && e.job.tracks.length === 0) : !!e.error));
  const jobTracks = entries.flatMap((e) => (e.job?.status === 'succeeded' ? e.job.tracks : []));
  // Just-finished sounds lead; never more than the latest RECENT_LIMIT (the rest is in Your Collections › All sounds).
  const shown = dedupe([...jobTracks, ...listTracks]).slice(0, RECENT_LIMIT);
  const hasRows = pending.length + failed.length + shown.length > 0;

  const changeScope = (next: SoundsScope) => {
    setScope(next);
    onScopeChange?.(next);
  };

  return (
    <section
      id="audio-recent"
      aria-labelledby="audio-recent-title"
      className="mt-8 min-w-0 scroll-mt-6 lg:col-start-2 lg:row-span-3 lg:row-start-1 lg:mt-0 lg:grid lg:grid-rows-subgrid"
    >
      {/* Row A: title + note (shared with "Trending on TikTok"). */}
      <div className="min-w-0">
        <h2 id="audio-recent-title" className="flex h-7 min-w-0 items-center gap-1.5 text-[17px] font-semibold leading-7 text-[#101828]">
          Recent creations
        </h2>
        <p className="mt-1 text-[12.5px] leading-[18px] text-[#667085]">{RECENT_NOTE}</p>
        <OriginalsNote />
      </div>

      {/* Row B: toolbar. */}
      <div data-toolbar="sounds" className="mt-3 flex h-8 items-center gap-2">
        <ScopeToggle value={scope} onChange={changeScope} />
        <Button size="sm" variant="ghost" onClick={() => onSeeAll(scope)} className="ml-auto h-8 shrink-0 rounded-[8px] px-2.5 text-[12.5px] text-[#6941c6]">See all</Button>
      </div>

      {/* Row C: the panel — its top edge lines up with the first row of trend cards. */}
      <aside
        aria-label="Stage"
        className="mt-4 min-w-0 self-start overflow-hidden rounded-[12px] border border-[#eaecf0] bg-white lg:sticky lg:top-4 lg:max-h-[calc(100dvh-6rem-var(--player-inset))] lg:overflow-y-auto lg:overscroll-contain lg:[scrollbar-gutter:stable]"
        style={{ '--player-inset': `${inset}px` } as CSSProperties}
      >
        <div hidden={Boolean(stage)}>
          {hasRows && (
            <ul ref={listRef} className="divide-y divide-[#f2f4f7]" aria-label="Recent creations list">
              {pending.map(({ ref, job }) => (
                <JobRow key={ref.jobId} name={job?.title || 'Composing…'} subtitle={jobSubtitle(ref, job)} progress={job?.progress ?? 0} />
              ))}
              {failed.map(({ ref, job, error }) => (
                <FailedJobRow
                  key={ref.jobId}
                  name={job?.title || jobSubject(ref)}
                  error={error ? `Could not check this generation: ${error.message}` : `Failed — ${job?.error || 'something went wrong. Try again, or tweak the prompt.'}`}
                  onRetry={ref.request ? () => void retry(ref) : undefined}
                  retrying={retrying === ref.jobId}
                  onDismiss={() => onDismissJob(ref.jobId)}
                />
              ))}
              {shown.map((t) => (
                <SoundRow key={t.track_id} track={t} actions={actions} showOwner={scope === 'team'} isNew={fresh.has(t.track_id)} flash={flash.has(t.track_id)} now={now} />
              ))}
            </ul>
          )}
          {tracks.isLoading && !shown.length ? (
            <div className="flex items-center justify-center gap-2 py-8 text-[12.5px] text-[#667085]" role="status"><Loader2 className="h-4 w-4 animate-spin text-[#98a2b3]" aria-hidden /> Loading sounds…</div>
          ) : tracks.isError ? (
            <div className="border-t border-[#f2f4f7] px-4 py-6 text-center text-[12.5px] text-[#b42318] first:border-t-0" role="alert">Could not load sounds: {(tracks.error as Error).message}</div>
          ) : !hasRows ? (
            <div className="px-6 py-10 text-center text-[12.5px] leading-[18px] text-[#667085]">{EMPTY_SOUNDS_TEXT}</div>
          ) : null}
        </div>
        {stage}
        {/* Portalled dialogs — kept inside the panel so nothing lands in the section's grid rows. */}
        {actions.dialogs}
      </aside>
    </section>
  );
}
